import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';

import {EMPTY, of, tap} from 'rxjs';
import {catchError, filter, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {
  addBuildKit,
  addBuildKitFailure,
  addBuildKitSuccess,
  addCadModelRevisionQuantity,
  addCadModelRevisionQuantityFailure,
  addCadModelRevisionQuantitySuccess,
  addTraveler,
  addTravelerFailure,
  addTravelerSuccess,
  loadBuildKits,
  loadBuildKitsFailure,
  loadBuildKitsNoInclude,
  loadBuildKitsSuccess,
  loadCadModels,
  loadCadModelsFailure,
  loadCadModelsSuccess,
  loadTravelerCategories,
  loadTravelerCategoriesFailure,
  loadTravelerCategoriesSuccess,
  modifyBuildKit,
  modifyBuildKitFailure,
  modifyBuildKitSettingFailure,
  modifyBuildKitSettings,
  modifyBuildKitSettingSuccess,
  modifyBuildKitSuccess,
  modifyCadModelRevisionQuantity,
  modifyCadModelRevisionQuantityFailure,
  modifyCadModelRevisionQuantitySuccess,
  modifyTraveler,
  modifyTravelerFailure,
  modifyTravelerSuccess,
  removeBuildKit,
  removeBuildKitFailure,
  removeBuildKitSuccess,
  removeCadModelRevisionQuantity,
  removeCadModelRevisionQuantityFailure,
  removeCadModelRevisionQuantitySuccess,
  removeTraveler,
  removeTravelerFailure,
  removeTravelerSuccess,
  setSelectedBuildKitFailure,
  setSelectedBuildKitSuccess,
  loadArchivedBuildKits,
  loadArchivedBuildKitsSuccess,
  loadArchivedBuildKitsFailure,
  archiveBuildKit,
  archiveBuildKitSuccess,
  archiveBuildKitFailure,
  unArchiveBuildKitSuccess,
  unArchiveBuildKitFailure,
  unArchiveBuildKit,
  updateBuildKitFlags,
} from "./build-kit.action";
import {
  toggleDrawingRedlineSuccess,
  toggleDrawingNonConformanceSuccess,
} from "../../../cad-model-upload/state/cad-model-revision-drawing/cad-model-revision-drawing.action";
import { selectCadModelRevisionDrawingEntities, selectBuildKitDrawingsMap } from "../../../cad-model-upload/state/cad-model-revision-drawing/cad-model-revision-drawing.selectors";
import {AppState} from "../../../shared/state/app.state";
import {BuildKitService} from "../../data-access/build-kit.service";
import {selectAllBuildKits, selectBuildKits} from "./build-kit.selectors";
import {TravelerCategoryService} from "../../data-access/traveler-category.service";
import {CadModelService} from "../../../cad-model-upload/data-access/cad-model.service";
import {selectDefaultBuildKitSettingTemplate} from "../build-kit-setting-template/build-kit-setting-template.selectors";
import {Router} from "@angular/router";
import {ToastMessageOptions, MessageService} from "primeng/api";
import {GetMessage} from "../../../global-config/utils/toast-messages.utils";

@Injectable()
export class BuildKitEffects {

  loadBuildKits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBuildKits),
      switchMap(() =>
        this.buildKitService.get()
          .pipe(
            map((buildKits) => loadBuildKitsSuccess({buildKits: buildKits})),
            catchError((error) => of(loadBuildKitsFailure({ error: GetMessage('LoadFailure', 'Build Kit') })))
          )
      )
    )
  );

  loadBuildKitsNoInclude$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBuildKitsNoInclude),
      switchMap(() =>
        this.buildKitService.getNoInclude()
          .pipe(
            map((buildKits) => loadBuildKitsSuccess({buildKits: buildKits})),
            catchError((error) => of(loadBuildKitsFailure({ error: GetMessage('LoadFailure', 'Part Numbers') })))
          )
      )
    )
  );

  loadTravelerCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTravelerCategories),
      switchMap(() =>
        this.travelerCategoryService.get()
          .pipe(
            map((travelerCategories) => loadTravelerCategoriesSuccess({travelerCategories: travelerCategories})),
            catchError((error) => of(loadTravelerCategoriesFailure({ error: GetMessage('LoadFailure', 'Build Kit') })))
          )
      )
    )
  );

  modifyBuildKits$ = createEffect(() =>
      this.actions$.pipe(
        ofType(modifyBuildKit),
        withLatestFrom(this.store.select(selectBuildKits)),
        switchMap(([action, buildKits]) =>
          this.buildKitService.update(action.buildKit)
            .pipe(
              map((buildKit) => modifyBuildKitSuccess({buildKit: buildKit, successMessage: GetMessage('ModifySuccess', 'Build Kit')})),
              catchError((error) => of(modifyBuildKitFailure({ error: GetMessage('ModifyFailure', 'Build Kit') })))
            )
        )
      )
  );

  modifyCadModelQuantities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyCadModelRevisionQuantity),
      withLatestFrom(this.store.select(selectBuildKits)),
      switchMap(([action, buildKits]) =>
        this.buildKitService.updateCadModelRevisionQuantity(action.cadModelRevisionQuantity)
          .pipe(
            map((buildKits) => modifyCadModelRevisionQuantitySuccess({successMessage: GetMessage('ModifySuccess', 'CAD Data Quantity')})),
            catchError((error) => of(modifyCadModelRevisionQuantityFailure({ error: GetMessage('ModifyFailure', 'CAD Data Quantity') })))
          )
      )
    )
  );

  modifyTravelers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyTraveler),
      withLatestFrom(this.store.select(selectBuildKits)),
      switchMap(([action, buildKits]) =>
        this.buildKitService.updateTraveler(action.traveler)
          .pipe(
            map((buildKits) => modifyTravelerSuccess({traveler: action.traveler, successMessage: GetMessage('ModifySuccess', 'Traveler')})),
            catchError((error) => of(modifyTravelerFailure({ traveler: action.traveler, error: GetMessage('ModifyFailure', 'Traveler') })))
          )
      )
    )
  );

  modifyBuildKitSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyBuildKitSettings),
      withLatestFrom(this.store.select(selectBuildKits)),
      switchMap(([action, buildKits]) =>
        this.buildKitService.update({...buildKits.buildKits.find(x => x.id === action.buildKitId), settings: action.buildKitSettings})
          .pipe(
            map((buildKit) => modifyBuildKitSettingSuccess({successMessage: GetMessage('ModifySuccess', 'Settings')})),
            catchError((error) => of(modifyBuildKitSettingFailure({ error: GetMessage('ModifyFailure', 'Settings') })))
          )
      )
    )
  );

  removeCadModelRevisionQuantity = createEffect(() =>
    this.actions$.pipe(
      ofType(removeCadModelRevisionQuantity),
      withLatestFrom(this.store.select(selectBuildKits)),
      switchMap(([action, buildKits]) => {
       return this.buildKitService.removeCadModelRevisionQuantity(action.cadModelRevisionQuantity.id)
         .pipe(
           map((buildKit) => removeCadModelRevisionQuantitySuccess({successMessage: GetMessage('RemoveSuccess', 'CAD Data Quantity')})),
           catchError((error) => of(removeCadModelRevisionQuantityFailure({ error: GetMessage('RemoveFailure', 'CAD Data Quantity') })))
         )

      })
    )
  );

  removeTraveler = createEffect(() =>
    this.actions$.pipe(
      ofType(removeTraveler),
      withLatestFrom(this.store.select(selectBuildKits)),
      switchMap(([action, buildKits]) => {
        return this.buildKitService.removeTraveler(action.traveler.id)
          .pipe(
            map((buildKit) => removeTravelerSuccess({successMessage: GetMessage('RemoveSuccess', 'Traveler')})),
            catchError((error) => of(removeTravelerFailure({ error: GetMessage('RemoveFailure', 'Traveler') })))
          )

      })
    )
  );

  createBuildKit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addBuildKit),
      withLatestFrom(this.store.select(selectDefaultBuildKitSettingTemplate)),
      switchMap(([action, defaultBuildKitSettingTemplate]) =>
        this.buildKitService.create({...action.buildKit, settings: defaultBuildKitSettingTemplate?.settings})
          .pipe(
            map((addedBuildKit) => addBuildKitSuccess({buildKit: addedBuildKit, successMessage: GetMessage('AddSuccess', 'Build Kit')})),
            catchError((error) => of(addBuildKitFailure({ error: GetMessage('AddFailure', 'Build Kit') })))
          )
      )
    )
  );

  selectBuildKitAfterAdding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addBuildKitSuccess),
      tap((action) =>
        {
          this.router.navigate([`/build-kit/${action.buildKit.id}/cad-data`]);
        }
      )
    ),{ dispatch: false }
  );

  createTraveler$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addTraveler),
      withLatestFrom(this.store.select(selectBuildKits)),
      switchMap(([action, buildKits]) =>
        this.buildKitService.createTraveler(action.traveler)
          .pipe(
            map((addedTraveler) => addTravelerSuccess({traveler: addedTraveler, successMessage: GetMessage('AddSuccess', 'Traveler')})),
            catchError((error) => of(addTravelerFailure({ error: GetMessage('AddFailure', 'Traveler') })))
          )
      )
    )
  );

  createCadModelRevisionQuantity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCadModelRevisionQuantity),
      withLatestFrom(this.store.select(selectBuildKits)),
      switchMap(([action, buildKits]) =>
        this.buildKitService.createCadModelRevisionQuantity(action.cadModelRevisionQuantity)
          .pipe(
            map((addedCadModelRevisionQuantity) => addCadModelRevisionQuantitySuccess({cadModelRevisionQuantity: addedCadModelRevisionQuantity, successMessage: GetMessage('AddSuccess', 'CAD Data Quantity')})),
            catchError((error) => of(addCadModelRevisionQuantityFailure({ error: GetMessage('AddFailure', 'CAD Data Quantity') })))
          )
      )
    )
  );

  removeBuildKit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeBuildKit),
      withLatestFrom(this.store.select(selectBuildKits)),
      switchMap(([action, buildKits]) =>
        this.buildKitService.remove(action.buildKitId)
          .pipe(
            map((removedBuildKitId) => removeBuildKitSuccess({buildKitId: removedBuildKitId, successMessage: GetMessage('RemoveSuccess', 'Build Kit')})),
            catchError((error) => of(removeBuildKitFailure({ error: GetMessage('RemoveFailure', 'Build Kit')})))
          )
      )
    )
  );

  archiveBuildKit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(archiveBuildKit),
      withLatestFrom(this.store.select(selectBuildKits)),
      switchMap(([action, buildKits]) =>
        this.buildKitService.archive(action.buildKitId)
          .pipe(
            map((archivedBuildKitId) => archiveBuildKitSuccess({buildKitId: archivedBuildKitId, successMessage: GetMessage('ArchiveSuccess', 'Build Kit')})),
            catchError((error) => of(archiveBuildKitFailure({ error: GetMessage('ArchiveFailure', 'Build Kit')})))
          )
      )
    )
  );

  unArchiveBuildKit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(unArchiveBuildKit),
      withLatestFrom(this.store.select(selectBuildKits)),
      switchMap(([action, buildKits]) =>
        this.buildKitService.unArchive(action.buildKitId)
          .pipe(
            map((archivedBuildKitId) => unArchiveBuildKitSuccess({buildKitId: archivedBuildKitId, successMessage: GetMessage('UnArchiveSuccess', 'Build Kit')})),
            catchError((error) => of(unArchiveBuildKitFailure({ error: GetMessage('UnArchiveFailure', 'Build Kit')})))
          )
      )
    )
  );

  setSelectedBuildKitFromAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addBuildKitSuccess),
      switchMap((action) =>
        this.store.select(selectAllBuildKits)
          .pipe(
            map((buildKits) => setSelectedBuildKitSuccess({ selectedBuildKit: buildKits.find(buildKit => buildKit.id == action.buildKit.id) ?? null })),
            catchError((error) => of(setSelectedBuildKitFailure({ error })))
          )
      )
    )
  );

  loadCadModels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCadModels),
      switchMap(() =>
        this.cadModelService.get()
          .pipe(
            map((cadModels) => loadCadModelsSuccess({cadModels: cadModels})),
            catchError((error) => of(loadCadModelsFailure({ error: GetMessage('LoadFailure', 'CAD Data') })))
          )
      )
    )
  );

  loadArchivedBuildKits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadArchivedBuildKits),
      switchMap(() =>
        this.buildKitService.getArchivedBuildKits().pipe(
          map((archivedBuildKits) => loadArchivedBuildKitsSuccess({ archivedBuildKits })),
          catchError((error) => of(loadArchivedBuildKitsFailure({ error: error.message })))
        )
      )
    )
  );

  toastSuccessMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        addBuildKitSuccess,
        modifyBuildKitSuccess,
        removeBuildKitSuccess,
        archiveBuildKitSuccess,
        addCadModelRevisionQuantitySuccess,
        modifyCadModelRevisionQuantitySuccess,
        removeCadModelRevisionQuantitySuccess,
        addTravelerSuccess,
        modifyTravelerSuccess,
        removeTravelerSuccess,
        modifyBuildKitSettingSuccess,
        unArchiveBuildKitSuccess,
      ),
      tap(({ successMessage }) => {
          return this.messageService.add({
            severity: 'success',
            summary: successMessage,
          } as ToastMessageOptions)
        }
      )
    ),
    { dispatch: false }
  );

  toastErrorMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          addBuildKitFailure,
          modifyBuildKitFailure,
          removeBuildKitFailure,
          archiveBuildKitFailure,

          addCadModelRevisionQuantityFailure,
          modifyCadModelRevisionQuantityFailure,
          removeCadModelRevisionQuantityFailure,

          addTravelerFailure,
          modifyTravelerFailure,
          removeTravelerFailure,

          modifyBuildKitSettingFailure,

          loadBuildKitsFailure,
          loadTravelerCategoriesFailure,
          loadCadModelsFailure,

          unArchiveBuildKitFailure
        ),
        tap(({ error }) => {
            return this.messageService.add({
              severity: 'error',
              summary: error,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  updateBuildKitFlagsOnDrawingToggle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleDrawingRedlineSuccess, toggleDrawingNonConformanceSuccess),
      withLatestFrom(
        this.store.select(selectAllBuildKits),
        this.store.select(selectCadModelRevisionDrawingEntities),
        this.store.select(selectBuildKitDrawingsMap),
      ),
      switchMap(([action, buildKits, drawingEntities, buildKitDrawingsMap]) => {
        const cadModelRevisionId = 'drawing' in action
          ? (action as any).drawing.cadModelRevisionId
          : (action as any).cadModelRevisionDrawing.id;
        const buildKit = buildKits.find(bk =>
          bk.cadModelRevisionQuantities.some(q => q.cadModelRevisionId === cadModelRevisionId)
        );
        if (!buildKit) return EMPTY;
        const revisionIds = [...new Set(buildKit.cadModelRevisionQuantities.map(q => q.cadModelRevisionId))];
        let allDrawings = revisionIds.flatMap(id => (drawingEntities as any)[id]?.drawings ?? []);
        if (allDrawings.length === 0) {
          allDrawings = (buildKitDrawingsMap as any)[buildKit.id] ?? [];
        }
        const isRedline = allDrawings.some((d: any) => d.isRedline);
        const isNonConformance = allDrawings.some((d: any) => d.isNonConformance);
        return of(updateBuildKitFlags({ buildKitId: buildKit.id, isRedline, isNonConformance }));
      }),
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private buildKitService: BuildKitService,
    private cadModelService: CadModelService,
    private travelerCategoryService: TravelerCategoryService,
    private router: Router,
    private messageService: MessageService
  ) {}
}

